import {
  parseRuntimeConfig,
  RUNTIME_CONFIG_FILE,
  type ConfigProblem,
  type RuntimeConfig,
} from '@/lib/config/runtime-config-schema'

export type { ConfigProblem, RuntimeConfig } from '@/lib/config/runtime-config-schema'

/*
 * Runtime configuration (ADR-034): each deployment serves its own
 * /app-config.json beside the same build, and the app loads it once before it
 * renders. Nothing here has a default, so a missing or broken file stops the
 * app at startup instead of falling back to another environment.
 */

export const RUNTIME_CONFIG_PATH = `/${RUNTIME_CONFIG_FILE}`

export class RuntimeConfigError extends Error {
  readonly problems: ConfigProblem[]

  constructor(problems: ConfigProblem[]) {
    super(`Invalid ${RUNTIME_CONFIG_FILE}: ${problems.map((p) => `${p.field} (${p.reason})`).join(', ')}`)
    this.name = 'RuntimeConfigError'
    this.problems = problems
  }
}

let current: RuntimeConfig | null = null

/** Validates and holds a configuration. Loopback http BFF origins are accepted only in development builds. */
export function setRuntimeConfig(raw: unknown): RuntimeConfig {
  const result = parseRuntimeConfig(raw, { allowLoopbackHttp: import.meta.env.DEV })
  if (!result.ok) throw new RuntimeConfigError(result.problems)
  current = result.config
  return current
}

/** Fetches, validates and holds /app-config.json. Throws RuntimeConfigError naming what is wrong. */
export async function loadRuntimeConfig(fetchImpl: typeof fetch = fetch): Promise<RuntimeConfig> {
  const unreachable = () => new RuntimeConfigError([{ field: RUNTIME_CONFIG_FILE, reason: 'unreachable' }])

  let response: Response
  try {
    response = await fetchImpl(RUNTIME_CONFIG_PATH, { cache: 'no-store', headers: { Accept: 'application/json' } })
  } catch {
    throw unreachable()
  }
  if (!response.ok) throw unreachable()

  let raw: unknown
  try {
    raw = await response.json()
  } catch {
    throw new RuntimeConfigError([{ field: RUNTIME_CONFIG_FILE, reason: 'malformed' }])
  }
  return setRuntimeConfig(raw)
}

/** The loaded configuration. Calling this before loading is a programming error. */
export function getRuntimeConfig(): RuntimeConfig {
  if (!current) throw new Error('Runtime configuration has not been loaded')
  return current
}

/** Forgets the loaded configuration (tests only). */
export function clearRuntimeConfig(): void {
  current = null
}
